import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, Text, Loader, Center, Group, Badge } from "@mantine/core";

export interface WayfindMarker {
  room_id: number;
  room_name: string;
  floor_name: string;
  floor_id: number | null;
  centroid: [number, number] | null;
  geometry?: Record<string, unknown> | null;
}

export interface WayfindFloorplan {
  id: number;
  name: string;
  viewbox: string;
  scale: number;
  /** Pre-sanitized SVG body (already cleaned server-side via crm.utils.clean_svg). */
  svg_content: string;
  navigation_paths: Array<Array<[number, number]>>;
  navigation_nodes: Array<{ id?: string; x: number; y: number; name?: string; type?: string }>;
}

export interface WayfindData {
  same_floorplan: boolean;
  same_floor: boolean;
  floorplan: WayfindFloorplan | null;
  origin: WayfindMarker | null;
  destination: WayfindMarker;
}

export interface WayfindingPanelProps {
  loading?: boolean;
  data: WayfindData | null;
  height?: number | string;
  showCaption?: boolean;
  /** When set, render the floorplan + markers as a server-pre-rendered
   *  `<img src={svgUrl}>` instead of building the SVG client-side.
   *  Recommended for slow kiosk devices (IAdea, low-end Android) where
   *  parsing a large floorplan SVG and reconciling React markers on
   *  every tap is expensive. Tenant portal in a desktop browser can
   *  keep using the data-driven path for richer interactivity. */
  svgUrl?: string | null;
}

function parseViewbox(vb: string | undefined | null): [number, number, number, number] | null {
  if (!vb) return null;
  const parts = vb.trim().split(/\s+/).map(Number);
  if (parts.length !== 4 || parts.some((p) => Number.isNaN(p))) return null;
  return parts as [number, number, number, number];
}

/** Shared caption used by both the data-driven and the svgUrl render
 *  modes. Pure read of the WayfindData markers — no SVG/DOM access. */
const WayfindCaption: React.FC<{ data: WayfindData }> = ({ data }) => {
  const { origin, destination } = data;
  return (
    <Group gap="xs">
      {origin ? (
        <Text fz="xs" c="dimmed">
          From <Text component="span" fw={600}>{origin.room_name}</Text>
          {" → "}
          <Text component="span" fw={600}>{destination.room_name}</Text>
        </Text>
      ) : (
        <Text fz="xs" c="dimmed">
          Walk to <Text component="span" fw={600}>{destination.room_name}</Text>
        </Text>
      )}
      {!data.same_floor && origin && (
        <Badge size="xs" color="orange">Different floor</Badge>
      )}
    </Group>
  );
};

function extractSvgInner(svg: string): string {
  const open = svg.match(/<svg[^>]*>/);
  if (!open) return svg;
  const start = (open.index ?? 0) + open[0].length;
  const end = svg.lastIndexOf("</svg>");
  if (end === -1) return svg.slice(start);
  return svg.slice(start, end);
}

/**
 * Lightweight floorplan + origin/destination markers + dotted route.
 *
 * Renders the FloorPlan.svg_content inline (already sanitized by
 * crm.utils.clean_svg server-side) and overlays SVG circles at the room
 * centroids. If the floorplan exposes navigation_paths, we draw them
 * faintly so the user can mentally trace the corridor; otherwise we
 * just draw a straight dashed line between origin and destination.
 *
 * The same trust model as `@nodus/floorplan` FloorplanViewer applies —
 * SVGs are authored by site admins and re-cleaned on every save.
 */
export const WayfindingPanel: React.FC<WayfindingPanelProps> = ({
  loading = false,
  data,
  height = 320,
  showCaption = true,
  svgUrl,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerW, setContainerW] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (e) setContainerW(e.contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Use dangerouslySetInnerHTML so React mounts the children in the SVG
  // namespace (matches packages/floorplan FloorplanViewer:682).
  // svg_content is sanitized server-side via crm.utils.clean_svg.
  const svgBody = useMemo(() => {
    if (!data?.floorplan) return "";
    const inner = extractSvgInner(data.floorplan.svg_content);
    if (!inner && data.floorplan.svg_content) {
      // Surface malformed SVG so we get a Sentry breadcrumb instead of a
      // half-rendered floorplan.
      // eslint-disable-next-line no-console
      console.warn("WayfindingPanel: could not extract SVG body");
    }
    return inner;
  }, [data?.floorplan]);

  // svgUrl wins: server has already drawn the floorplan + markers + route
  // into a single SVG document, so the kiosk only has to decode + paint
  // (no parsing the floorplan body, no React reconciliation per tap).
  if (svgUrl) {
    return (
      <Stack gap="xs" ref={containerRef}>
        {showCaption && data && <WayfindCaption data={data} />}
        <Box
          style={{
            width: "100%",
            height,
            borderRadius: 8,
            overflow: "hidden",
            background: "#1a1d23",
          }}
        >
          <img
            src={svgUrl}
            alt="Wayfind"
            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          />
        </Box>
      </Stack>
    );
  }

  if (loading) {
    return (
      <Stack gap="xs" ref={containerRef}>
        <Center style={{ height }}>
          <Loader size="sm" />
        </Center>
      </Stack>
    );
  }
  if (!data) {
    return (
      <Stack gap="xs" ref={containerRef}>
        <Center style={{ height }}>
          <Text c="dimmed" fz="sm">No route data.</Text>
        </Center>
      </Stack>
    );
  }

  const fp = data.floorplan;
  const dest = data.destination;
  const origin = data.origin;

  // Without a floorplan or destination centroid we can't render a map —
  // fall back to a small text card. The modal still has the row's data
  // available, so this is graceful.
  if (!fp || !dest.centroid) {
    return (
      <Stack gap="xs" ref={containerRef}>
        {showCaption && (
          <Text fz="xs" c="dimmed">
            Walk to <Text component="span" fw={600}>{dest.room_name}</Text>
          </Text>
        )}
        <Center style={{ height }}>
          <Stack gap={4} align="center">
            <Text fz="sm">{dest.room_name}</Text>
            <Text fz="xs" c="dimmed">{dest.floor_name}</Text>
            {!data.same_floor && origin && (
              <Badge color="orange">Different floor than {origin.floor_name}</Badge>
            )}
          </Stack>
        </Center>
      </Stack>
    );
  }

  const vb = parseViewbox(fp.viewbox) ?? [0, 0, 1000, 1000];
  const [, , vbW, vbH] = vb;
  const w = containerW || 480;
  const aspect = vbH / Math.max(vbW, 1);
  const renderH = Math.min(typeof height === "number" ? height : 320, w * aspect);

  // Cheap polyline route from navigation_paths: draws every walkable
  // segment faintly so the user can mentally connect the dots. Same
  // coordinate-space caveat as lineD — only render when both endpoints
  // belong to this floorplan.
  const routeD =
    data.same_floorplan && origin?.centroid && dest.centroid && fp.navigation_paths?.length > 0
      ? fp.navigation_paths
          .map((pts) => pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" "))
          .join(" ")
      : null;

  // Only meaningful when origin + destination share the same floorplan —
  // otherwise the centroids are in different SVG coordinate spaces and the
  // line is misleading. The caption ("Different floor") tells the user.
  const lineD =
    data.same_floorplan && origin?.centroid && dest.centroid
      ? `M${origin.centroid[0]},${origin.centroid[1]} L${dest.centroid[0]},${dest.centroid[1]}`
      : null;

  return (
    <Stack gap="xs" ref={containerRef}>
      {showCaption && <WayfindCaption data={data} />}
      <Box style={{ width: "100%", height: renderH, position: "relative" }}>
        <svg
          viewBox={fp.viewbox || `0 0 ${vbW} ${vbH}`}
          preserveAspectRatio="xMidYMid meet"
          width="100%"
          height="100%"
          style={{ background: "#1a1d23", borderRadius: 8 }}
        >
          <g opacity={0.45} dangerouslySetInnerHTML={{ __html: svgBody }} />
          {routeD && (
            <path
              d={routeD}
              fill="none"
              stroke="#34d399"
              strokeOpacity={0.55}
              strokeWidth={Math.max(2, vbW / 250)}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          {lineD && (
            <path
              d={lineD}
              fill="none"
              stroke="#fbbf24"
              strokeWidth={Math.max(2, vbW / 300)}
              strokeDasharray={`${vbW / 100} ${vbW / 200}`}
            />
          )}
          {data.same_floorplan && origin?.centroid && (
            <circle
              cx={origin.centroid[0]}
              cy={origin.centroid[1]}
              r={Math.max(6, vbW / 80)}
              fill="#3b82f6"
              stroke="#0d1117"
              strokeWidth={2}
            />
          )}
          {dest.centroid && (
            <circle
              cx={dest.centroid[0]}
              cy={dest.centroid[1]}
              r={Math.max(8, vbW / 60)}
              fill="#ef4444"
              stroke="#0d1117"
              strokeWidth={2}
            />
          )}
        </svg>
        <Group
          gap="xs"
          style={{
            position: "absolute",
            bottom: 8,
            right: 8,
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(4px)",
            padding: "4px 8px",
            borderRadius: 6,
          }}
        >
          {origin && (
            <Group gap={4}>
              <Box w={10} h={10} style={{ borderRadius: "50%", background: "#3b82f6" }} />
              <Text fz="xs">You</Text>
            </Group>
          )}
          <Group gap={4}>
            <Box w={10} h={10} style={{ borderRadius: "50%", background: "#ef4444" }} />
            <Text fz="xs">Target</Text>
          </Group>
        </Group>
      </Box>
    </Stack>
  );
};
